/*
  Storefront helper for custom Shopify events.
  Pushes directly to window.dataLayer — bypasses pixel sandbox entirely.
  Include in theme.liquid before </body>.
*/

(function () {
    var persistentTrackKeys = [
        "page_location",
        "page_referrer",
        "page_title",
        "page_hostname",
        "ecomm_pagetype",
        "user_data"
    ];
    var cartStateEventNames = {
        add_to_cart: true,
        quick_add: true
    };

    var disabledTrackEventNames = {
        view_item: true,
        add_to_cart: true,
        remove_from_cart: true,
        select_item: true
    };

    var changeValueStore = {};
    var previousUrlStorageKey = "__noosh_previous_url";
    var lastKnownUrl = typeof window !== "undefined" && window.location ? window.location.href : "";
    var previousUrl = null;

    function readSessionStorageValue(key) {
        try {
            return window.sessionStorage.getItem(key);
        } catch (_err) {
            return null;
        }
    }

    function writeSessionStorageValue(key, value) {
        try {
            if (value == null || value === "") {
                window.sessionStorage.removeItem(key);
                return;
            }

            window.sessionStorage.setItem(key, value);
        } catch (_err) {
            return;
        }
    }

    function normalizeTrackingSlug(value) {
        return readText(String(value || ""))
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");
    }

    var popupIntentState = null;
    var popupIntentTtlMs = 30000;
    var klaviyoEventCache = {};

    function readElementSignature(el) {
        if (!(el instanceof Element)) return "";

        return readText([
            el.textContent,
            el.getAttribute("aria-label"),
            el.getAttribute("name"),
            el.getAttribute("id"),
            el.getAttribute("class"),
            el.getAttribute("href"),
            el.getAttribute("data-testid"),
            el.getAttribute("data-form-id"),
            el.getAttribute("data-type"),
            el.getAttribute("type")
        ].join(" "));
    }

    function isBackInStockSignature(signature) {
        return /(notify|back in stock|back_in_stock|backinstock|restock|waitlist|sold out|sold_out|out of stock|out_of_stock)/i.test(signature);
    }

    function isNewsletterSignature(signature) {
        return /(newsletter|subscribe|sign up|signup|welcome|discount|coupon|get 15 off|get_15_off|email|sms)/i.test(signature);
    }

    function buildPopupTrackingContext(kind, options) {
        var provider = options && options.provider === "klaviyo" ? "klaviyo" : "default";

        if (kind === "back_in_stock") {
            return {
                kind: kind,
                leadType: "back_in_stock",
                elementPrefix: "back_in_stock_popup",
                formName: provider === "klaviyo" ? "klaviyo_back_in_stock_popup" : "back_in_stock_popup",
                source: provider === "klaviyo" ? "klaviyo_back_in_stock_popup" : "back_in_stock_popup"
            };
        }

        return {
            kind: "newsletter",
            leadType: "newsletter",
            elementPrefix: "newsletter_popup",
            formName: provider === "klaviyo" ? "klaviyo_newsletter_popup" : "newsletter_popup",
            source: provider === "klaviyo" ? "klaviyo_newsletter_popup" : "homepage_newsletter_popup"
        };
    }

    function detectPopupKindFromSignature(signature) {
        if (!signature) return null;
        if (isBackInStockSignature(signature)) return "back_in_stock";
        if (isNewsletterSignature(signature)) return "newsletter";
        return null;
    }

    function rememberPopupIntent(kind) {
        if (!kind) return;

        popupIntentState = {
            kind: kind,
            expiresAt: Date.now() + popupIntentTtlMs
        };
    }

    function readRememberedPopupIntent() {
        if (!popupIntentState) return null;
        if (popupIntentState.expiresAt < Date.now()) {
            popupIntentState = null;
            return null;
        }

        return popupIntentState.kind;
    }

    function detectPopupKindFromElement(el) {
        if (!(el instanceof Element)) return null;

        var candidates = uniqueElements([
            el,
            el.closest("form"),
            el.closest("[role='dialog']"),
            el.closest(".klaviyo-form"),
            el.closest("#ic-welcome-popup")
        ]);

        for (var i = 0; i < candidates.length; i += 1) {
            var candidate = candidates[i];
            if (!(candidate instanceof Element)) continue;

            var kind = detectPopupKindFromSignature(readElementSignature(candidate));
            if (kind) return kind;
        }

        return null;
    }

    function readPopupTrackingContext(popupRoot, options) {
        var provider = options && options.provider === "klaviyo" ? "klaviyo" : "default";
        var fallbackKind = "newsletter";

        if (popupRoot instanceof Element && popupRoot.dataset.nooshPopupKind) {
            return buildPopupTrackingContext(popupRoot.dataset.nooshPopupKind, { provider: provider });
        }

        var popupKind = detectPopupKindFromElement(popupRoot) || readRememberedPopupIntent() || fallbackKind;

        if (popupRoot instanceof Element) {
            popupRoot.dataset.nooshPopupKind = popupKind;
        }

        return buildPopupTrackingContext(popupKind, { provider: provider });
    }

    function readKlaviyoEventEmail(detail) {
        if (!detail || typeof detail !== "object") return "";

        var metaData = detail.metaData && typeof detail.metaData === "object" ? detail.metaData : {};
        return readText(String(
            metaData.email ||
            metaData.$email ||
            detail.email ||
            ""
        ));
    }

    function readKlaviyoPopupContextFromDetail(detail) {
        var signature = readText([
            detail && detail.formId,
            detail && detail.companyId,
            detail && detail.type,
            detail && detail.metaData ? JSON.stringify(detail.metaData) : ""
        ].join(" "));
        var popupKind = detectPopupKindFromSignature(signature) || readRememberedPopupIntent() || "newsletter";

        return buildPopupTrackingContext(popupKind, { provider: "klaviyo" });
    }

    function shouldTrackKlaviyoEvent(detail, eventType) {
        var formId = readText(String(detail && detail.formId || ""));
        var cacheKey = [eventType, formId || "unknown"].join("::");
        var now = Date.now();
        var lastTrackedAt = klaviyoEventCache[cacheKey] || 0;

        if (now - lastTrackedAt < 1000) {
            return false;
        }

        klaviyoEventCache[cacheKey] = now;
        return true;
    }

    function setPreviousUrl(url) {
        if (!url) return;
        previousUrl = url;
        writeSessionStorageValue(previousUrlStorageKey, url);
    }

    function readPreviousUrl() {
        var candidateUrl = previousUrl || readSessionStorageValue(previousUrlStorageKey) || document.referrer || null;

        if (!candidateUrl || candidateUrl === window.location.href) {
            return null;
        }

        return candidateUrl;
    }

    function initPreviousUrlTracking() {
        if (typeof window === "undefined" || typeof document === "undefined") return;

        previousUrl = readSessionStorageValue(previousUrlStorageKey) || document.referrer || null;

        if (previousUrl === window.location.href) {
            previousUrl = null;
        }

        if (typeof window.history === "object") {
            ["pushState", "replaceState"].forEach(function (methodName) {
                var originalMethod = window.history[methodName];
                if (typeof originalMethod !== "function") return;

                window.history[methodName] = function () {
                    var currentUrl = window.location.href;
                    var result = originalMethod.apply(this, arguments);

                    if (currentUrl !== window.location.href) {
                        setPreviousUrl(currentUrl);
                        lastKnownUrl = window.location.href;
                    }

                    return result;
                };
            });
        }

        window.addEventListener("popstate", function () {
            if (lastKnownUrl && lastKnownUrl !== window.location.href) {
                setPreviousUrl(lastKnownUrl);
            }

            lastKnownUrl = window.location.href;
        });

        window.addEventListener("hashchange", function () {
            if (lastKnownUrl && lastKnownUrl !== window.location.href) {
                setPreviousUrl(lastKnownUrl);
            }

            lastKnownUrl = window.location.href;
        });

        window.addEventListener("pagehide", function () {
            writeSessionStorageValue(previousUrlStorageKey, window.location.href);
        });
    }

    initPreviousUrlTracking();

    function readText(value) {
        if (typeof value !== "string") return "";
        return value.replace(/\s+/g, " ").trim();
    }

    function padDatePart(value) {
        return String(value).padStart(2, "0");
    }

    function getEventDateString(date) {
        return [
            date.getFullYear(),
            padDatePart(date.getMonth() + 1),
            padDatePart(date.getDate())
        ].join("-");
    }

    function generateEventId() {
        try {
            if (window.crypto && typeof window.crypto.randomUUID === "function") {
                return window.crypto.randomUUID();
            }
        } catch (_err) {
            // no-op
        }

        return [
            Date.now().toString(36),
            Math.random().toString(36).slice(2, 10)
        ].join("-");
    }

    function uniqueElements(elements) {
        return elements.filter(function (element, index) {
            return element && elements.indexOf(element) === index;
        });
    }

    function sanitizeTrackValue(value) {
        if (value == null) return undefined;

        if (Array.isArray(value)) {
            return value
                .map(sanitizeTrackValue)
                .filter(function (item) {
                    return item !== undefined;
                });
        }

        if (typeof value === "object") {
            var sanitizedObject = {};

            Object.keys(value).forEach(function (key) {
                var sanitizedValue = sanitizeTrackValue(value[key]);
                if (sanitizedValue !== undefined) {
                    sanitizedObject[key] = sanitizedValue;
                }
            });

            return sanitizedObject;
        }

        return value;
    }

    function sanitizeTrackDetail(eventName, detail) {
        var normalizedDetail = detail && typeof detail === "object" ? sanitizeTrackValue(Object.assign({}, detail)) : {};
        var now = new Date();

        if (!normalizedDetail || Array.isArray(normalizedDetail)) {
            normalizedDetail = {};
        }

        if (normalizedDetail.page_location == null && normalizedDetail.actual_url != null) {
            normalizedDetail.page_location = normalizedDetail.actual_url;
        }

        if (normalizedDetail.page_referrer == null && normalizedDetail.previous_url != null) {
            normalizedDetail.page_referrer = normalizedDetail.previous_url;
        }

        if (typeof window.location !== "undefined") {
            normalizedDetail.page_location = window.location.href;
            normalizedDetail.page_hostname = window.location.hostname;
        }

        if (normalizedDetail.page_referrer == null) {
            var pageReferrer = readPreviousUrl();
            if (pageReferrer) {
                normalizedDetail.page_referrer = pageReferrer;
            }
        }

        normalizedDetail.event_name = "track_" + eventName;
        normalizedDetail.event_timestamp = now.getTime();
        normalizedDetail.event_date = getEventDateString(now);

        if (normalizedDetail.event_id == null) {
            normalizedDetail.event_id = generateEventId();
        }

        if (typeof document !== "undefined" && normalizedDetail.page_title == null && document.title) {
            normalizedDetail.page_title = document.title;
        }

        if (normalizedDetail.cta_text == null) {
            if (normalizedDetail.link_name != null) {
                normalizedDetail.cta_text = normalizedDetail.link_name;
            } else if (normalizedDetail.creative_name != null) {
                normalizedDetail.cta_text = normalizedDetail.creative_name;
            }
        }

        if (!cartStateEventNames[eventName]) {
            delete normalizedDetail.cart_state;
        }

        if (normalizedDetail.color_selected != null) {
            delete normalizedDetail.next_value;
            delete normalizedDetail.previous_color_selected;
            delete normalizedDetail.next_color_selected;
            delete normalizedDetail.cta_text;
            delete normalizedDetail.link_name;
        }

        if (normalizedDetail.source === "pdp") {
            delete normalizedDetail.item_list_name;
            delete normalizedDetail.item_count;
            delete normalizedDetail.items;
            delete normalizedDetail.index;
        }

        if (normalizedDetail.source != null) {
            if (normalizedDetail.tracking_source == null) {
                normalizedDetail.tracking_source = normalizedDetail.source;
            }

            delete normalizedDetail.source;
        }

        delete normalizedDetail.actual_url;
        delete normalizedDetail.previous_url;
        delete normalizedDetail.link_name;
        delete normalizedDetail.creative_name;

        return sanitizeTrackValue(normalizedDetail) || {};
    }

    function resetGtmState(keysToPreserve) {
        var dl = window.dataLayer = window.dataLayer || [];
        dl.push(function () {
            if (typeof this.reset !== "function" || typeof this.get !== "function" || typeof this.set !== "function") return;

            var preservedValues = {};

            (keysToPreserve || []).forEach(function (key) {
                var value = this.get(key);
                if (value !== undefined) {
                    preservedValues[key] = value;
                }
            }, this);

            this.reset();

            Object.keys(preservedValues).forEach(function (key) {
                this.set(key, preservedValues[key]);
            }, this);
        });
    }

    window.nooshTrack = function nooshTrack(eventName, detail) {
        if (!eventName || disabledTrackEventNames[eventName]) return;

        var rawDetail = detail && typeof detail === "object" ? Object.assign({}, detail) : {};
        var triggerEl = rawDetail.__trackTriggerEl instanceof Element ? rawDetail.__trackTriggerEl : null;
        var dl = window.dataLayer = window.dataLayer || [];
        var keysToPreserve = persistentTrackKeys.slice();

        delete rawDetail.__trackTriggerEl;

        if (triggerEl instanceof Element) {
            enrichInteractionMetadata(triggerEl, rawDetail, triggerEl);
        }

        var normalizedDetail = sanitizeTrackDetail(eventName, rawDetail);

        if (cartStateEventNames[eventName]) {
            keysToPreserve.push("cart_state");
        }

        resetGtmState(keysToPreserve);

        dl.push({ ecommerce: null });
        dl.push(Object.assign({ event: "track_" + eventName }, normalizedDetail));
    };

    function parseTrackJson(value) {
        if (!value) return {};

        try {
            return JSON.parse(value);
        } catch (_err) {
            return {};
        }
    }

    function readDynamicValue(el) {
        if (!el) return null;

        if (el.dataset.trackValue) {
            return el.dataset.trackValue;
        }

        if (el instanceof HTMLSelectElement) {
            var option = el.selectedOptions && el.selectedOptions[0];
            if (option) {
                return option.dataset.trackValue || option.value;
            }
        }

        if (
            el instanceof HTMLInputElement ||
            el instanceof HTMLSelectElement ||
            el instanceof HTMLTextAreaElement
        ) {
            return el.value;
        }

        return (el.textContent || "").trim();
    }

    function buildChangeValueKey(el) {
        if (!(el instanceof Element)) return null;

        if (
            (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) &&
            el.name
        ) {
            var scopeEl =
                el.closest("[data-product-id], [data-testid='product-information-details'], dialog-component, quick-add-dialog") ||
                el.form ||
                el.closest("form") ||
                document.body;
            var scopeKey = scopeEl instanceof Element
                ? scopeEl.id || scopeEl.getAttribute("data-product-id") || scopeEl.getAttribute("data-testid") || scopeEl.tagName
                : "document";

            return [scopeKey, el.name].join("::");
        }

        return null;
    }

    function readCurrentChangeValue(el) {
        if (!(el instanceof Element)) return null;

        if (el instanceof HTMLInputElement && el.type === "radio" && el.name) {
            var scopeEl =
                el.closest("[data-product-id], [data-testid='product-information-details'], dialog-component, quick-add-dialog") ||
                el.form ||
                el.closest("form") ||
                document;
            var checkedValue = null;

            scopeEl.querySelectorAll('input[type="radio"]').forEach(function (input) {
                if (!(input instanceof HTMLInputElement)) return;
                if (input.name !== el.name || input.checked !== true) return;
                checkedValue = readDynamicValue(input);
            });

            return checkedValue != null ? checkedValue : readDynamicValue(el);
        }

        return readDynamicValue(el);
    }

    function readPreviousChangeValue(el) {
        var changeKey = buildChangeValueKey(el);

        if (changeKey && changeValueStore[changeKey] != null) {
            return changeValueStore[changeKey];
        }

        return el && el.dataset ? el.dataset.trackPreviousValue : null;
    }

    function storePreviousChangeValue(el, value) {
        if (!el || !el.dataset || value == null) return;

        var normalizedValue = String(value);
        var changeKey = buildChangeValueKey(el);

        el.dataset.trackPreviousValue = normalizedValue;

        if (changeKey) {
            changeValueStore[changeKey] = normalizedValue;
        }
    }

    function readClosestHref(el) {
        if (!(el instanceof Element)) return null;

        var hrefElement = el.closest("a[href], area[href], form[action]");
        if (!hrefElement) return null;

        if (hrefElement instanceof HTMLAnchorElement || hrefElement instanceof HTMLAreaElement) {
            return hrefElement.href || null;
        }

        if (hrefElement instanceof HTMLFormElement) {
            return hrefElement.action || null;
        }

        return null;
    }

    function readElementLabel(el, params) {
        if (!(el instanceof Element)) return null;

        var isValueElement =
            el instanceof HTMLInputElement ||
            el instanceof HTMLSelectElement ||
            el instanceof HTMLTextAreaElement;
        var isTextInteractiveElement =
            el.matches("a, area, button, summary, [role='button'], [role='link']");

        var candidates = [
            params.cta_text,
            params.link_name,
            params.creative_name,
            el.dataset.trackLabel,
            el.getAttribute("aria-label"),
            el.getAttribute("title"),
            el instanceof HTMLButtonElement ? el.value : null,
            isValueElement ? readDynamicValue(el) : null,
            isTextInteractiveElement ? readText(el.textContent || "") : null
        ];

        for (var i = 0; i < candidates.length; i += 1) {
            var candidate = readText(String(candidates[i] || ""));
            if (candidate) return candidate;
        }

        return null;
    }

    function readClosestSectionName(el, params) {
        if (!(el instanceof Element)) return null;
        if (readText(String(params.section_name || ""))) return params.section_name;

        var explicitSection = el.closest("[data-track-section-name]");
        if (explicitSection instanceof HTMLElement) {
            var explicitSectionName = readText(explicitSection.dataset.trackSectionName || "");
            if (explicitSectionName) return explicitSectionName;
        }

        var headingSelectors =
            '[data-track-section-title], .section-header h1, .section-header h2, .section-header h3, .section-header h4, .section__heading, .section-resource-list__header h1, .section-resource-list__header h2, .section-resource-list__header h3, .section-resource-list__header h4, h1, h2, h3, h4';
        var scopedContainers = uniqueElements([
            el.closest("slideshow-slide"),
            el.closest(".slide__content"),
            el.closest(".group-block-content"),
            el.closest(".group-block"),
            el.closest(".hero__content-wrapper"),
            el.closest(".section-resource-list"),
            el.closest('[data-testid="product-information-details"]')
        ]);

        for (var i = 0; i < scopedContainers.length; i += 1) {
            var scopedContainer = scopedContainers[i];
            if (!(scopedContainer instanceof Element)) continue;

            var scopedHeading = scopedContainer.querySelector(headingSelectors);
            if (scopedHeading instanceof Element) {
                var scopedHeadingText = readText(scopedHeading.textContent || "");
                if (scopedHeadingText) return scopedHeadingText;
            }
        }

        var container = el.closest(
            '[data-testid="product-information-details"], section, [id^="shopify-section-"], .shopify-section, .section, dialog-component'
        );

        if (container instanceof Element) {
            var heading = container.querySelector(headingSelectors);

            if (heading instanceof Element) {
                var headingText = readText(heading.textContent || "");
                if (headingText) return headingText;
            }
        }

        if (readText(String(params.item_list_name || ""))) return params.item_list_name;

        return null;
    }

    function readClosestProductData(el, params) {
        var productId = params.item_id != null ? params.item_id : null;
        var productName = params.item_name != null ? params.item_name : null;

        if (!(el instanceof Element)) {
            return {
                product_id: productId,
                product_name: productName
            };
        }

        if (productId != null && productName != null) {
            return {
                product_id: productId,
                product_name: productName
            };
        }

        var detailSources = uniqueElements([
            el.closest('[data-testid="product-information-details"][data-track-params]'),
            el.closest("product-card, .resource-card, .collection-card")
                ? el.closest("product-card, .resource-card, .collection-card").querySelector(
                    ".product-card__link[data-track-params], .resource-card__link[data-track-params]"
                )
                : null
        ]);

        for (var i = 0; i < detailSources.length; i += 1) {
            var trackSource = detailSources[i];
            if (!(trackSource instanceof HTMLElement)) continue;

            var sourceParams = parseTrackJson(trackSource.dataset.trackParams);
            if (productId == null && sourceParams.item_id != null) {
                productId = sourceParams.item_id;
            }
            if (productName == null && sourceParams.item_name != null) {
                productName = sourceParams.item_name;
            }
        }

        if (productId == null) {
            var productIdEl = el.closest("[data-product-id]");
            if (productIdEl instanceof HTMLElement && productIdEl.dataset.productId) {
                productId = productIdEl.dataset.productId;
            }
        }

        return {
            product_id: productId,
            product_name: productName
        };
    }

    function normalizeExtraParams(extraParams) {
        if (!extraParams || typeof extraParams !== "object") {
            return {
                params: {},
                triggerEl: null
            };
        }

        var normalizedParams = Object.assign({}, extraParams);
        var triggerEl = normalizedParams.__trackTriggerEl instanceof Element ? normalizedParams.__trackTriggerEl : null;

        delete normalizedParams.__trackTriggerEl;

        return {
            params: normalizedParams,
            triggerEl: triggerEl
        };
    }

    function enrichInteractionMetadata(el, params, triggerEl) {
        var interactionEl = triggerEl instanceof Element ? triggerEl : el;
        var nextClickedUrl = readClosestHref(interactionEl);
        var ctaText = readElementLabel(interactionEl, params);
        var sectionName = readClosestSectionName(interactionEl, params);
        var productData = readClosestProductData(interactionEl, params);
        var productDetails = interactionEl.closest('[data-testid="product-information-details"]');

        if (nextClickedUrl && params.next_clicked_url == null) {
            params.next_clicked_url = nextClickedUrl;
        }

        if (ctaText && params.cta_text == null) {
            params.cta_text = ctaText;
        }

        if (sectionName && params.section_name == null) {
            params.section_name = sectionName;
        }

        if (params.section_name != null && params.location_id != null && params.section_name === params.location_id) {
            delete params.section_name;
        }

        if (params.product_id == null && productData.product_id != null) {
            params.product_id = productData.product_id;
        }

        if (params.product_name == null && productData.product_name != null) {
            params.product_name = productData.product_name;
        }

        if (
            params.content_type === "product_variant" &&
            params.interaction_location !== "quick_add_popup" &&
            productDetails instanceof Element
        ) {
            params.source = "pdp";
            delete params.item_list_name;
            delete params.item_count;
            delete params.items;
            delete params.index;
        }

        return params;
    }

    function readNumber(value) {
        var parsedValue = Number(value);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    }

    function enrichFormParams(el, params) {
        if (!(el instanceof HTMLFormElement)) return params;

        var searchInput = el.querySelector('[name="q"]');
        if (searchInput instanceof HTMLInputElement || searchInput instanceof HTMLTextAreaElement) {
            var searchTerm = readText(searchInput.value || "");
            if (searchTerm && params.search_term == null) {
                params.search_term = searchTerm;
            }
        }

        var quantityInput = el.querySelector('[name="quantity"]');
        if (quantityInput) {
            var quantityValue = Number(quantityInput.value);
            params.quantity = Number.isNaN(quantityValue) ? quantityInput.value : quantityValue;
        }

        var variantInput = el.querySelector('[name="id"]');
        if (variantInput && params.variant_id == null) {
            params.variant_id = variantInput.value;
        }

        if (typeof params.price === "number" && typeof params.quantity === "number") {
            params.value = Number((params.price * params.quantity).toFixed(2));
        }

        return params;
    }

    function buildTrackParams(el, extraParams) {
        var params = {};
        var contextEl = el.closest("[data-track-context]");
        var normalizedExtraParams = normalizeExtraParams(extraParams);

        if (contextEl) {
            Object.assign(params, parseTrackJson(contextEl.dataset.trackContext));
        }

        Object.assign(params, parseTrackJson(el.dataset.trackParams));

        if (el.dataset.trackValueKey) {
            params[el.dataset.trackValueKey] = readDynamicValue(el);
        }

        Object.assign(params, normalizedExtraParams.params);

        if (typeof params.unit_price === "number" && typeof params.quantity_delta === "number" && params.value == null) {
            params.value = Number((params.unit_price * Math.abs(params.quantity_delta)).toFixed(2));
        }

        if (params.unit_price != null) {
            delete params.unit_price;
        }

        enrichInteractionMetadata(el, params, normalizedExtraParams.triggerEl);

        return enrichFormParams(el, params);
    }

    function fireTrack(el, eventName, extraParams) {
        if (!eventName) return;
        window.nooshTrack(eventName, buildTrackParams(el, extraParams));
    }

    function fireTrackList(el, eventNames, extraParams) {
        if (!eventNames) return;

        String(eventNames)
            .split(",")
            .map(function (eventName) {
                return eventName.trim();
            })
            .filter(Boolean)
            .forEach(function (eventName) {
                fireTrack(el, eventName, extraParams);
            });
    }

    function initChangeTrack(el) {
        if (!el || el.dataset.trackChangeInitialized === "1") return;

        el.dataset.trackChangeInitialized = "1";
        storePreviousChangeValue(el, readCurrentChangeValue(el));
    }

    function readTrackDelta(el) {
        if (!el || !el.dataset.trackDeltaKey) return null;

        var currentValue = readNumber(readCurrentChangeValue(el));
        if (currentValue == null) return null;

        var previousValue = readNumber(readPreviousChangeValue(el));
        if (previousValue == null) {
            previousValue = currentValue;
        }

        return {
            currentValue: currentValue,
            delta: currentValue - previousValue,
            key: el.dataset.trackDeltaKey
        };
    }

    function resolveChangeEventName(el, deltaInfo) {
        if (deltaInfo) {
            if (deltaInfo.delta > 0 && el.dataset.trackChangePositive) {
                return el.dataset.trackChangePositive;
            }

            if (deltaInfo.delta < 0 && el.dataset.trackChangeNegative) {
                return el.dataset.trackChangeNegative;
            }

            if (deltaInfo.delta === 0) {
                return null;
            }
        }

        return el.dataset.trackChange;
    }

    // Examples:
    // window.nooshTrack("ui_interaction", { interaction_type: "navigation", element: "search_icon" });
    // window.nooshTrack("select_promotion", { promotion_id: "hero_main", creative_slot: "slide_1" });
    // window.nooshTrack("form_start", { form_name: "track_order" });
    // window.nooshTrack("form_submit", { form_name: "track_order", status: "success" });
    // window.nooshTrack("view_size_chart", { item_id: "gid://shopify/Product/12345" });

    /*
      DOM add-on: data-attribute click delegation
      ─────────────────────────────────────────────
      Add these two HTML attributes to any element to track it — no per-element JS needed:
    
        data-track="<eventName>"
        data-track-params='{"key":"value"}'   ← optional JSON
    
      Examples:
        <a data-track="select_promotion"
           data-track-params='{"promotion_id":"hero_main","creative_slot":"slide_1","cta_text":"play_sheer_launch","location_id":"homepage_hero"}'>
          Shop Now
        </a>
    
        <button data-track="ui_interaction"
                data-track-params='{"interaction_type":"navigation","element":"search_icon"}'>
    
        <button data-track="ui_interaction"
                data-track-params='{"interaction_type":"filter","filter_category":"shade","filter_value":"Noir"}'>
    
        <button data-track="generate_lead"
                data-track-params='{"lead_type":"newsletter","source":"welcome_popup_15_off","coupon":"WELCOME15"}'>
    */

    // ── Click delegation (data-track) ──────────────────────────────────────────
    // Uses capture phase (true) so stopPropagation() on child handlers can't block it
    document.addEventListener("click", function (e) {
        if (!(e.target instanceof Element)) return;

        var popupTrigger = e.target.closest("button, a, input[type='submit'], input[type='button']");
        if (popupTrigger instanceof Element) {
            var popupKind = detectPopupKindFromElement(popupTrigger);
            if (popupKind) {
                rememberPopupIntent(popupKind);
            }
        }

        var el = e.target.closest("[data-track]");
        if (el) {
            fireTrack(el, el.dataset.track);

            if (el.matches(".resource-card__link")) {
                var resourceCard = el.closest(".resource-card[data-track]");
                if (resourceCard instanceof HTMLElement && resourceCard !== el) {
                    fireTrack(resourceCard, resourceCard.dataset.track, {
                        __trackTriggerEl: el
                    });
                }
            }

            return;
        }

        var card = e.target.closest("product-card, .resource-card, .collection-card");
        if (!card) return;
        if (card instanceof HTMLElement && card.hasAttribute("data-no-navigation")) return;

        var interactiveElement = e.target.closest("button, input, label, select, a, [tabindex='1']");
        var trackedLink = card.querySelector(".product-card__link[data-track], .resource-card__link[data-track], .collection-card__link[data-track]");

        if (!(trackedLink instanceof HTMLElement)) return;
        if (interactiveElement && interactiveElement !== trackedLink) return;

        fireTrack(trackedLink, trackedLink.dataset.track);
    }, true);

    // ── Focusin-once delegation (data-track-once) ───────────────────────────────
    // Fires the event exactly once when a user first interacts with the container.
    // Usage: add data-track-once="form_start" data-track-params='{"form_name":"contact"}'
    // to any wrapper element (e.g. the contact form div).
    document.addEventListener("focusin", function (e) {
        var el = e.target.closest("[data-track-once]");
        if (!el) return;
        if (el.dataset.trackOnceFired) return;

        el.dataset.trackOnceFired = "1";
        fireTrack(el, el.dataset.trackOnce);
    });

    // ── Submit delegation (data-track-submit) ───────────────────────────────────
    document.addEventListener("submit", function (e) {
        var el = e.target.closest("[data-track-submit]");
        if (!el) return;
        fireTrackList(el, [el.dataset.trackSubmit, el.dataset.trackSubmitExtra].filter(Boolean).join(","), {
            __trackTriggerEl: e.submitter instanceof Element ? e.submitter : el
        });
    }, true);

    // ── Change delegation (data-track-change) ───────────────────────────────────
    document.addEventListener("change", function (e) {
        var el = e.target.closest("[data-track-change],[data-track-change-positive],[data-track-change-negative]");
        if (!el) return;

        if (el.dataset.trackSuppressOnce === "1") {
            delete el.dataset.trackSuppressOnce;
            storePreviousChangeValue(el, readCurrentChangeValue(el));
            return;
        }

        var deltaInfo = readTrackDelta(el);
        var eventName = resolveChangeEventName(el, deltaInfo);

        if (!eventName) {
            if (deltaInfo) {
                storePreviousChangeValue(el, deltaInfo.currentValue);
            }
            return;
        }

        var extraParams = null;
        var currentValue = readCurrentChangeValue(el);
        var previousValue = readPreviousChangeValue(el);

        if (deltaInfo) {
            extraParams = {};
            extraParams[deltaInfo.key] = deltaInfo.delta;
        }

        if (previousValue != null && currentValue != null) {
            extraParams = extraParams || {};
            extraParams.previous_value = previousValue;
            extraParams.next_value = String(currentValue);

            if (el.dataset.trackValueKey) {
                extraParams["previous_" + el.dataset.trackValueKey] = previousValue;
                extraParams["next_" + el.dataset.trackValueKey] = String(currentValue);
            }
        }

        extraParams = Object.assign({
            __trackTriggerEl: el
        }, extraParams || {});

        fireTrack(el, eventName, extraParams);
        storePreviousChangeValue(el, currentValue);
    }, true);

    document.addEventListener("click", function (e) {
        if (!(e.target instanceof Element)) return;

        var popupRoot = e.target.closest("#ic-welcome-popup");
        if (!(popupRoot instanceof Element)) return;

        var interactiveEl = e.target.closest("button, a, input[type='submit'], input[type='button']");
        if (!(interactiveEl instanceof Element)) return;

        var popupContext = readPopupTrackingContext(popupRoot);
        var label = readElementLabel(interactiveEl, {}) || interactiveEl.getAttribute("aria-label") || "";
        var normalizedLabel = normalizeTrackingSlug(label);
        var elementName = popupContext.elementPrefix + "_cta";

        if (interactiveEl.getAttribute("aria-label")) {
            elementName = popupContext.elementPrefix + "_close";
        } else if (normalizedLabel === "skip_for_now") {
            elementName = popupContext.elementPrefix + "_skip";
        } else if (normalizedLabel === "subscribe") {
            elementName = popupContext.elementPrefix + "_subscribe";
        } else if (normalizedLabel === "continue_shopping") {
            elementName = popupContext.elementPrefix + "_continue_shopping";
        } else if (normalizedLabel) {
            elementName = popupContext.elementPrefix + "_" + normalizedLabel;
        }

        fireTrack(interactiveEl, "ui_interaction", Object.assign(
            {
                interaction_type: "click",
                element: elementName,
                source: popupContext.source,
                lead_type: popupContext.leadType
            },
            emailValue ? { user_data: { email: emailValue } } : {}
        ));
    }, true);

    // ── Dialog open delegation (data-track-open) ────────────────────────────────
    document.addEventListener("dialog:open", function (e) {
        if (!(e.target instanceof Element)) return;

        var el = e.target.closest("[data-track-open]");
        if (!el) return;

        fireTrackList(el, [el.dataset.trackOpen, el.dataset.trackOpenExtra].filter(Boolean).join(","));
    }, true);
    // ── Klaviyo popup delegation ─────────────────────────────────────────────────
    document.addEventListener("click", function (e) {
        if (!(e.target instanceof Element)) return;

        // Check if click is inside a Klaviyo form
        var klaviyoForm = e.target.closest(".klaviyo-form");
        if (!(klaviyoForm instanceof Element)) return;

        var interactiveEl = e.target.closest("button, a, input[type='submit'], input[type='button']");
        if (!(interactiveEl instanceof Element)) return;

        var popupContext = readPopupTrackingContext(klaviyoForm, { provider: "klaviyo" });
        var ariaLabel = interactiveEl.getAttribute("aria-label") || "";
        var label = readElementLabel(interactiveEl, {}) || ariaLabel;
        var normalizedLabel = normalizeTrackingSlug(label);
        var elementName = popupContext.elementPrefix + "_cta";
        var isSubscribe = false;

        if (ariaLabel.toLowerCase().includes("close")) {
            elementName = popupContext.elementPrefix + "_close";
        } else if (
            normalizedLabel.includes("no") ||
            normalizedLabel.includes("don_t") ||
            normalizedLabel.includes("discount")
        ) {
            elementName = popupContext.elementPrefix + "_skip";
        } else if (
            normalizedLabel.includes("off") ||
            normalizedLabel.includes("get") ||
            normalizedLabel.includes("15") ||
            normalizedLabel.includes("notify") ||
            normalizedLabel.includes("submit")
        ) {
            elementName = popupContext.elementPrefix + "_subscribe";
            isSubscribe = true;
        } else if (normalizedLabel) {
            elementName = popupContext.elementPrefix + "_" + normalizedLabel;
        }

        var emailInput = klaviyoForm.querySelector("input[type='email']");
        var emailValue = emailInput ? emailInput.value.trim() : "";

        // Always fire ui_interaction for every button click in the popup
        fireTrack(interactiveEl, "ui_interaction", Object.assign(
            {
                interaction_type: "click",
                element: elementName,
                source: popupContext.source,
                lead_type: popupContext.leadType
            },
            emailValue ? { email: emailValue } : {}
        ));

        // Only fire generate_lead when subscribe button clicked with a valid email
        if (isSubscribe && emailValue) {
            var leadParams = {
                lead_type: popupContext.leadType,
                source: popupContext.source,
                user_data: { email: emailValue }
            };


            fireTrack(interactiveEl, "generate_lead", leadParams);
        }
    }, true);

    // ── Klaviyo popup form_start (first interaction with email field) ─────────────
    document.addEventListener("focusin", function (e) {
        if (!(e.target instanceof Element)) return;

        var emailInput = e.target.closest("input[type='email']");
        if (!(emailInput instanceof Element)) return;

        var klaviyoForm = emailInput.closest(".klaviyo-form");
        if (!(klaviyoForm instanceof Element)) return;

        // Only fire once per popup open
        if (klaviyoForm.dataset.trackFormStartFired === "1") return;
        klaviyoForm.dataset.trackFormStartFired = "1";

        var popupContext = readPopupTrackingContext(klaviyoForm, { provider: "klaviyo" });
        fireTrack(emailInput, "form_start", {
            form_name: popupContext.formName,
            lead_type: popupContext.leadType,
            source: popupContext.source
        });
    });

    // ── Klaviyo popup view (fires when popup appears in DOM) ─────────────────────
    new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            mutation.addedNodes.forEach(function (node) {
                if (!(node instanceof Element)) return;

                // Check if the added node is or contains a Klaviyo form
                var klaviyoForm = node.matches(".klaviyo-form")
                    ? node
                    : node.querySelector(".klaviyo-form");

                if (!(klaviyoForm instanceof Element)) return;
                if (klaviyoForm.dataset.trackViewFired === "1") return;

                klaviyoForm.dataset.trackViewFired = "1";
                var popupContext = readPopupTrackingContext(klaviyoForm, { provider: "klaviyo" });

                fireTrack(klaviyoForm, "ui_interaction", {
                    interaction_type: "view",
                    element: popupContext.elementPrefix,
                    source: popupContext.source,
                    lead_type: popupContext.leadType
                });
            });
        });
    }).observe(document.documentElement, {
        childList: true,
        subtree: true
    });

    // ── Klaviyo popup event bridge (covers iframe/app-managed popups) ────────────
    function handleKlaviyoFormsEvent(event) {
        var detail = event && event.detail;
        if (!detail || typeof detail !== "object") return;

        var popupContext = readKlaviyoPopupContextFromDetail(detail);
        var trackTarget = document.body || document.documentElement;
        if (!(trackTarget instanceof Element)) return;

        if (detail.type === "open" || detail.type === "embedOpen") {
            if (!shouldTrackKlaviyoEvent(detail, "view")) return;

            fireTrack(trackTarget, "ui_interaction", {
                interaction_type: "view",
                element: popupContext.elementPrefix,
                source: popupContext.source,
                lead_type: popupContext.leadType
            });
            return;
        }

        if (detail.type !== "submit") return;
        if (!shouldTrackKlaviyoEvent(detail, "submit")) return;

        var emailValue = readKlaviyoEventEmail(detail);
        var leadParams = {
            lead_type: popupContext.leadType,
            source: popupContext.source
        };

        fireTrack(trackTarget, "generate_lead", Object.assign(
            {},
            leadParams,
            emailValue ? { user_data: { email: emailValue } } : {}
        ));
    }

    window.addEventListener("klaviyoForms", handleKlaviyoFormsEvent);
    document.addEventListener("klaviyoForms", handleKlaviyoFormsEvent);

    // ── Slideshow selection delegation (data-track-slideshow) ───────────────────
    document.addEventListener("slideshow:select", function (e) {
        if (!(e.target instanceof Element)) return;
        if (!e.target.matches("[data-track-slideshow]")) return;
        if (!e.detail || e.detail.userInitiated !== true) return;

        var interactionType = e.detail.trigger === "drag" || e.detail.trigger === "scroll" ? "swipe" : "click";

        fireTrack(e.target, e.target.dataset.trackSlideshow, {
            interaction_type: interactionType,
            slide_number: e.detail.index + 1
        });
    }, true);

    // ── Accordion open click delegation (summary only) ───────────────────────────
    document.addEventListener("click", function (e) {
        if (!(e.target instanceof Element)) return;

        var el = e.target.closest("[data-track-accordion-open]");
        if (!el) return;

        var details = el.closest("details");
        if (!(details instanceof HTMLDetailsElement)) return;
        if (details.open) return;

        fireTrack(el, el.dataset.trackAccordionOpen);
    }, true);

    // ── Review stars delegation (Loox hash links) ───────────────────────────────
    document.addEventListener("click", function (e) {
        if (!(e.target instanceof Element)) return;

        var el = e.target.closest('a[href*="#looxReviews"]');
        if (!el) return;
        if (el.closest("[data-track]")) return;

        var productDetails = el.closest('[data-testid="product-information-details"][data-track-params]');
        var productParams = parseTrackJson(productDetails && productDetails.dataset.trackParams);

        if (productParams.items != null) {
            delete productParams.items;
        }

        fireTrack(el, "select_content", Object.assign({
            content_type: "reviews_summary"
        }, productParams));
    }, true);

    var viewObserver = "IntersectionObserver" in window
        ? new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                fireViewTrack(entry.target);
            });
        }, { threshold: 0.25 })
        : null;

    function fireViewTrack(el) {
        if (!el || el.dataset.trackViewFired === "1") return;

        el.dataset.trackViewFired = "1";
        fireTrack(el, el.dataset.trackView);

        if (viewObserver) {
            viewObserver.unobserve(el);
        }
    }

    function initViewTrack(el) {
        if (!el || el.dataset.trackViewInitialized === "1") return;

        el.dataset.trackViewInitialized = "1";

        if (el.dataset.trackViewMode === "viewport" && viewObserver) {
            viewObserver.observe(el);
            return;
        }

        fireViewTrack(el);
    }

    function initToggleTrack(el) {
        if (!el || el.dataset.trackToggleInitialized === "1") return;

        el.dataset.trackToggleInitialized = "1";
        el.addEventListener("toggle", function () {
            var toggleOn = el.dataset.trackToggleOn || "both";

            if (toggleOn === "open" && !el.open) return;
            if (toggleOn === "close" && el.open) return;

            fireTrack(el, el.dataset.trackToggle);
        });
    }

    function initTracking(root) {
        if (!root) return;

        if (root instanceof Element && root.matches("[data-track-view]")) {
            initViewTrack(root);
        }

        if (root instanceof Element && root.matches("[data-track-toggle]")) {
            initToggleTrack(root);
        }

        if (root instanceof Element && root.matches("[data-track-change],[data-track-change-positive],[data-track-change-negative]")) {
            initChangeTrack(root);
        }

        if (!(root instanceof Element || root instanceof Document)) return;

        root.querySelectorAll("[data-track-view]").forEach(initViewTrack);
        root.querySelectorAll("[data-track-toggle]").forEach(initToggleTrack);
        root.querySelectorAll("[data-track-change],[data-track-change-positive],[data-track-change-negative]").forEach(initChangeTrack);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function () {
            initTracking(document);
        }, { once: true });
    } else {
        initTracking(document);
    }

    new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            mutation.addedNodes.forEach(function (node) {
                if (!(node instanceof Element)) return;
                initTracking(node);
            });
        });
    }).observe(document.documentElement, {
        childList: true,
        subtree: true
    });
})();
